import { createSlice } from '@reduxjs/toolkit';

import { MAX_SELECTED_SYMBOLS } from '@/constants/constants';
import type { SymbolKey } from '@/pages/Markets/MarketsComponents/types';
import type { SelectedSymbol } from '@/types/symbols';
import { isSameSymbol, isSameSymbolIgnoringPeriod } from '@/utils/isSameSymbol';

import type { PayloadAction } from '@reduxjs/toolkit';

export const createSelectedSymbol = (key: SymbolKey): SelectedSymbol => {
  return {
    Code: key.Code,
    Period: key.Period,
    PeriodType: key.PeriodType,
    TimeRef: key.TimeRef
  };
};

type SymbolSelectorState = {
  symbols: SelectedSymbol[];
  limitReached: boolean;
  isForwardMode: boolean; // only enabled on Dashboard when FWD is selected
  isDashboardAppSelector: boolean; // true when used in Dashboard app context
  isDataHubSelector: boolean; // true when used in DataHub context
};

const initialState: SymbolSelectorState = {
  symbols: [],
  limitReached: false,
  isForwardMode: false,
  isDashboardAppSelector: false,
  isDataHubSelector: false
};

type SymbolAction = {
  symbol: SelectedSymbol;
};

type SymbolArrayAction = {
  symbols: SelectedSymbol[];
};

const symbolKey = (symbol: SelectedSymbol, isForwardMode = false) =>
  isForwardMode
    ? `${symbol.Code}-${symbol.PeriodType}-${symbol.TimeRef}`
    : `${symbol.Code}-${symbol.PeriodType}-${symbol.Period}-${symbol.TimeRef}`;

// Single-pass dedupe helper: preserves first occurrence order
const dedupeSymbols = (symbols: SelectedSymbol[], isForwardMode = false) => {
  const seen = new Set<string>();
  const out: SelectedSymbol[] = [];
  for (const s of symbols) {
    const k = symbolKey(s, isForwardMode);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(s);
    }
  }
  return out;
};

const symbolSelectorSlice = createSlice({
  name: 'symbolSelector',
  initialState,
  reducers: {
    setSymbols: (state, action: PayloadAction<SelectedSymbol[]>) => {
      state.symbols = action.payload;
    },
    addSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const { symbol } = payload;
      // Fast membership check using a Set of keys
      const keys = new Set(state.symbols.map(s => symbolKey(s, state.isForwardMode)));
      const k = symbolKey(symbol, state.isForwardMode);
      if (keys.has(k)) return;

      if (state.symbols.length >= MAX_SELECTED_SYMBOLS) {
        state.limitReached = true;
        return;
      }

      // Prepend new symbol to keep previous UX behavior
      state.symbols = [symbol, ...state.symbols];
    },

    addSymbolArray: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      const keys = new Set(state.symbols.map(s => symbolKey(s, state.isForwardMode)));
      const toAdd: SelectedSymbol[] = [];
      for (const s of payload.symbols) {
        const k = symbolKey(s, state.isForwardMode);
        if (keys.has(k)) continue;
        // check limit before adding
        if (state.symbols.length + toAdd.length >= MAX_SELECTED_SYMBOLS) {
          state.limitReached = true;
          break;
        }
        keys.add(k);
        toAdd.push(s);
      }
      if (toAdd.length) {
        state.symbols = [...state.symbols, ...toAdd];
      }
    },

    deleteSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const equals = state.isForwardMode ? isSameSymbolIgnoringPeriod : isSameSymbol;
      state.symbols = state.symbols.filter(s => !equals(s, payload.symbol));
      if (state.symbols.length < MAX_SELECTED_SYMBOLS) {
        state.limitReached = false;
      }
    },

    deleteSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      // Uses default comparison (with Period) for bulk deletion
      const deleteSet = new Set(
        payload.symbols.map(s => `${s.Code}-${s.PeriodType}-${s.Period}-${s.TimeRef}`)
      );
      state.symbols = state.symbols.filter(
        symbol =>
          !deleteSet.has(`${symbol.Code}-${symbol.PeriodType}-${symbol.Period}-${symbol.TimeRef}`)
      );
    },

    toggleSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const { symbol } = payload;
      const k = symbolKey(symbol, state.isForwardMode);
      const keys = new Set(state.symbols.map(s => symbolKey(s, state.isForwardMode)));
      const exists = keys.has(k);
      if (exists) {
        const equals = state.isForwardMode ? isSameSymbolIgnoringPeriod : isSameSymbol;
        state.symbols = state.symbols.filter(s => !equals(s, symbol));
        if (state.symbols.length < MAX_SELECTED_SYMBOLS) {
          state.limitReached = false;
        }
      } else if (state.symbols.length >= MAX_SELECTED_SYMBOLS) {
        state.limitReached = true;
      } else {
        // Prepend when adding via toggle to match previous behavior
        state.symbols = [symbol, ...state.symbols];
      }
    },

    // Toggle an array of symbols in a single pass to avoid repeated subscriber churn
    toggleSymbolsArray: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      const existingKeys = new Set(state.symbols.map(s => symbolKey(s, state.isForwardMode)));
      const toggles = payload.symbols;

      // Determine removals and additions without exceeding limit
      const toRemove = new Set<number>();
      const toAdd: SelectedSymbol[] = [];

      // Build index map for fast removal checks
      const indexByKey = new Map<string, number>();
      for (const s of state.symbols) {
        const idx = state.symbols.indexOf(s);
        indexByKey.set(symbolKey(s, state.isForwardMode), idx);
      }

      for (const sym of toggles) {
        const k = symbolKey(sym, state.isForwardMode);
        if (existingKeys.has(k)) {
          const idx = indexByKey.get(k);
          if (idx != null) toRemove.add(idx);
        } else {
          if (state.symbols.length - toRemove.size + toAdd.length >= MAX_SELECTED_SYMBOLS) {
            state.limitReached = true;
            continue;
          }
          toAdd.push(sym);
        }
      }

      if (toRemove.size || toAdd.length) {
        const next: SelectedSymbol[] = [];
        // Keep order while removing
        for (const s of state.symbols) {
          const idx = state.symbols.indexOf(s);
          if (!toRemove.has(idx)) next.push(s);
        }
        // Prepend additions to match previous UX
        state.symbols = [...toAdd, ...next];
        if (state.symbols.length < MAX_SELECTED_SYMBOLS) {
          state.limitReached = false;
        }
      }
    },

    clearSelection: state => {
      state.symbols = [];
      state.limitReached = false;
    },

    dismissLimit: state => {
      state.limitReached = false;
    },

    setForwardMode: (state, action: PayloadAction<boolean>) => {
      const nextMode = action.payload;
      if (state.isForwardMode === nextMode) return;
      state.isForwardMode = nextMode;
      // Rebuild keys once and de-duplicate in a single pass according to the new mode
      state.symbols = dedupeSymbols(state.symbols, state.isForwardMode);
    },
    setIsDashboardAppSelector: (state, action: PayloadAction<boolean>) => {
      state.isDashboardAppSelector = action.payload;
    },
    setIsDataHubSelector: (state, action: PayloadAction<boolean>) => {
      state.isDataHubSelector = action.payload;
    }
  }
});

export const {
  setSymbols,
  addSymbol,
  addSymbolArray,
  deleteSymbol,
  clearSelection,
  dismissLimit,
  deleteSymbols,
  toggleSymbol,
  toggleSymbolsArray,
  setForwardMode,
  setIsDashboardAppSelector,
  setIsDataHubSelector
} = symbolSelectorSlice.actions;

export default symbolSelectorSlice.reducer;
